import {
  AnswerResponseProgress,
  type CyberAssessmentAnswerSelectDto,
  CyberAssessmentQuestionKind,
} from '@@types/api.types';
import { AssessmentProgressQuestion } from '@components/pages/assessment-shell/components/assessment-progress-question.component';
import { useInnovatorPatchCyberAssessmentAnswer } from '@queries/cyber-assessment.query';
import { type CyberAssessmentQuestionLike } from '@utils/cyber-assessment/model.util';
import { useEffect, useState } from 'react';

type CyberAssessmentQuestionComponentProps = {
  answer?: CyberAssessmentAnswerSelectDto;
  cyberAssessmentId: string;
  isReadOnly?: boolean;
  onNext: () => Promise<void> | void;
  onPrevious: () => Promise<void> | void;
  previousQuestion?: boolean;
  question: CyberAssessmentQuestionLike;
  questionNumber: number;
};

const OPTION_VALUES = [
  AnswerResponseProgress.YES,
  AnswerResponseProgress.NO,
  AnswerResponseProgress.IN_PROGRESS,
  AnswerResponseProgress.NOT_APPLICABLE,
] as const;
const PURPLE_TEAM_OPTION_LABELS: Partial<Record<AnswerResponseProgress, string>> = {
  [AnswerResponseProgress.IN_PROGRESS]: 'In Progress',
  [AnswerResponseProgress.NO]: 'Not Completed',
  [AnswerResponseProgress.YES]: 'Completed',
};
const SCRDF_QUESTION_ID_PATTERN = /^[A-Z]\d+\.\d+\.[A-Z]$/;

export const CyberAssessmentQuestionComponent: React.FC<CyberAssessmentQuestionComponentProps> = (props) => {
  const {
    answer,
    cyberAssessmentId,
    isReadOnly = false,
    onNext,
    onPrevious,
    previousQuestion,
    question,
    questionNumber,
  } = props;
  const { mutateAsync: patchCyberAssessmentAnswer, isPending } = useInnovatorPatchCyberAssessmentAnswer();
  const [details, setDetails] = useState(answer?.details ?? '');
  const [selectedOption, setSelectedOption] = useState(answer?.response);
  const optionValues =
    question.kind === CyberAssessmentQuestionKind.PurpleTeamExercise
      ? OPTION_VALUES.filter((option) => option !== AnswerResponseProgress.NOT_APPLICABLE)
      : OPTION_VALUES;

  useEffect(() => {
    setDetails(answer?.details ?? '');
    setSelectedOption(answer?.response);
  }, [answer?.details, answer?.response]);

  const persistAnswer = async (response: AnswerResponseProgress | undefined): Promise<void> => {
    if (isReadOnly || !response) {
      return;
    }

    await patchCyberAssessmentAnswer({
      cyberAssessmentId,
      details: details.trim() || undefined,
      questionId: question.id,
      response,
    });
  };

  return (
    <AssessmentProgressQuestion
      description={question.explanation}
      details={details}
      detailsPlaceholder={
        isReadOnly ? 'Completed cyber assessments are read-only' : 'Add implementation or remediation notes'
      }
      disableDetails={isReadOnly || !selectedOption || isPending}
      disabled={isReadOnly || isPending}
      footerNote={
        isReadOnly ? 'Completed cyber assessments are read-only. Use navigation to review saved answers.' : undefined
      }
      isNextLoading={isPending}
      isPreviousLoading={isPending}
      onDetailsBlur={() => persistAnswer(selectedOption)}
      onDetailsChange={setDetails}
      onNext={async () => {
        await persistAnswer(selectedOption);
        await onNext();
      }}
      onOptionChange={async (option) => {
        if (isReadOnly) {
          return;
        }

        const nextOption = optionValues.find((value) => value === option);

        if (!nextOption) {
          return;
        }

        setSelectedOption(nextOption);
        await patchCyberAssessmentAnswer({
          cyberAssessmentId,
          details: details.trim() || undefined,
          questionId: question.id,
          response: nextOption,
        });
      }}
      onPrevious={async () => {
        await persistAnswer(selectedOption);
        await onPrevious();
      }}
      optionLabels={
        question.kind === CyberAssessmentQuestionKind.PurpleTeamExercise ? PURPLE_TEAM_OPTION_LABELS : undefined
      }
      optionValues={optionValues}
      previousQuestion={previousQuestion}
      prompt={question.prompt}
      questionIdentifier={SCRDF_QUESTION_ID_PATTERN.test(question.id) ? question.id : undefined}
      questionNumber={questionNumber}
      selectedOption={selectedOption}
    />
  );
};
